/**
 * Com s'obren els links de WhatsApp **en aquest dispositiu**
 * (`plans/expedia-precontact.md` §F9 i §10.12, `plans/dia-a-dia-calendari-i-whatsapp.md` §3.3).
 *
 * El problema: `whatsappLinkFormat` viu a `messaging_config` i és **global**, però
 * en Pere fa servir WhatsApp Web a Windows i l'app al mòbil. Una sola config per a
 * tots dos no pot ser bona per a cap. I el user agent no basta: a Windows no
 * distingeix qui vol el navegador de qui vol l'app d'escriptori.
 *
 * La solució és no tocar res del servidor: el backend segueix generant el link que
 * digui `messaging_config` i el navegador el **reescriu** just abans d'obrir-lo,
 * segons una preferència desada a `localStorage` d'aquest navegador. Per defecte
 * `auto`, que des de la tanda «dia a dia» fa com el PMS vell: a l'ordinador,
 * WhatsApp Web; al mòbil o a la tauleta, `wa.me` (que obre l'app). Abans `auto` era
 * «el que digui el servidor», i al PC sortia la pàgina intermèdia de `wa.me`.
 */

import { STORAGE_KEYS, storage } from '@/lib/storage';

/**
 * Com vol aquest dispositiu que s'obrin els links.
 *
 * - `auto` — segons el dispositiu: ordinador → `web`, mòbil o tauleta → `mobile`. Per defecte.
 * - `web` — WhatsApp Web, en una pestanya del navegador.
 * - `desktop` — l'app d'escriptori, per l'esquema `whatsapp://`.
 * - `mobile` — el link universal `wa.me`, que al telèfon obre l'app.
 */
export type WhatsappOpenMode = 'auto' | 'web' | 'desktop' | 'mobile';

export const WHATSAPP_OPEN_MODES: WhatsappOpenMode[] = ['auto', 'web', 'desktop', 'mobile'];

/** Etiquetes per al selector de Configuració. */
export const WHATSAPP_OPEN_MODE_LABEL: Record<WhatsappOpenMode, string> = {
  auto: 'Automàtic (ordinador → WhatsApp Web, mòbil → wa.me)',
  web: 'WhatsApp Web (web.whatsapp.com)',
  desktop: "App d'escriptori (whatsapp://)",
  mobile: 'Mòbil (wa.me)',
};

/** Número i text d'un link de WhatsApp, sigui quin sigui el format d'origen. */
interface WhatsappTarget {
  /** Només dígits, sense `+`. */
  phone: string;
  /** El text preparat, ja descodificat. `null` = el link no en portava. */
  text: string | null;
}

/**
 * Treu número i text d'un link de WhatsApp. `null` si no és cap dels que coneixem.
 *
 * Els tres que genera el backend (`messaging/utils/whatsapp-link.util.ts`):
 * `wa.me/<n>?text=`, `web.whatsapp.com/send?phone=&text=` i
 * `whatsapp://send?phone=&text=`. Qualsevol altra cosa es deixa passar sense tocar:
 * reescriure un link que no s'entén seria pitjor que no reescriure'l.
 */
function parseWhatsappLink(url: string): WhatsappTarget | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const digits = (value: string | null) => (value ?? '').replace(/\D/g, '');
  const text = parsed.searchParams.get('text');

  // `wa.me/<número>` — el número va al camí, no a la query. Es descodifica abans
  // de treure'n els dígits: `URL` percent-codifica el que hi hagi al camí, i un
  // `%20` sense descodificar deixaria un `20` enmig del número.
  if (parsed.protocol === 'https:' && parsed.hostname === 'wa.me') {
    let pathname = parsed.pathname;
    try {
      pathname = decodeURIComponent(pathname);
    } catch {
      // Percentatge mal format: val el camí tal com és.
    }
    const phone = digits(pathname);
    return phone ? { phone, text } : null;
  }

  // `web.whatsapp.com/send` i `whatsapp://send` — el número va a `?phone=`.
  const isWeb = parsed.protocol === 'https:' && parsed.hostname === 'web.whatsapp.com';
  const isApp = parsed.protocol === 'whatsapp:';
  if (isWeb || isApp) {
    const phone = digits(parsed.searchParams.get('phone'));
    return phone ? { phone, text } : null;
  }

  return null;
}

/** Torna a muntar el link en el format que demana el dispositiu. */
function formatWhatsappLink(target: WhatsappTarget, mode: Exclude<WhatsappOpenMode, 'auto'>): string {
  const base =
    mode === 'web'
      ? `https://web.whatsapp.com/send?phone=${target.phone}`
      : mode === 'desktop'
        ? `whatsapp://send?phone=${target.phone}`
        : `https://wa.me/${target.phone}`;

  if (target.text === null) return base;

  // `wa.me` porta el número al camí i la query encara és verge; els altres dos ja
  // tenen el `?phone=` posat.
  const separator = mode === 'mobile' ? '?' : '&';
  return `${base}${separator}text=${encodeURIComponent(target.text)}`;
}

/** Quina mena de dispositiu és aquest, per al mode `auto`. */
export type WhatsappDevice = 'desktop' | 'mobile';

/** Mòbils i tauletes pel user agent. L'iPad d'ara diu «Macintosh»: el caça el `pointer: coarse`. */
const MOBILE_USER_AGENT = /Android|iPhone|iPad|iPod|Mobile|Tablet|Silk|Kindle|Opera Mini|IEMobile/i;

/**
 * Mòbil o ordinador, a partir del user agent i de si el punter principal és el dit
 * (`matchMedia('(pointer: coarse)')`). Pur, per als tests.
 *
 * Un portàtil amb pantalla tàctil té el ratolí o el trackpad com a punter principal
 * (`fine`) i un user agent d'escriptori: queda com a ordinador, que és el que vol.
 */
export function classifyWhatsappDevice(
  userAgent: string | null | undefined,
  coarsePointer: boolean,
): WhatsappDevice {
  if (coarsePointer) return 'mobile';
  return MOBILE_USER_AGENT.test(userAgent ?? '') ? 'mobile' : 'desktop';
}

/**
 * El dispositiu d'aquest navegador. Sense `window` (tests, render de servidor) torna
 * `mobile`: `wa.me` és l'únic format que funciona a tot arreu.
 */
export function detectWhatsappDevice(): WhatsappDevice {
  if (typeof window === 'undefined') return 'mobile';
  let coarse = false;
  try {
    coarse = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  } catch {
    // Un `matchMedia` que peta no ha de tombar el botó: es decideix pel user agent.
  }
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return classifyWhatsappDevice(userAgent, coarse);
}

/** El format concret que toca: `auto` es resol segons el dispositiu. */
export function resolveWhatsappOpenMode(
  mode: WhatsappOpenMode,
  device: WhatsappDevice,
): Exclude<WhatsappOpenMode, 'auto'> {
  if (mode !== 'auto') return mode;
  return device === 'mobile' ? 'mobile' : 'web';
}

/**
 * El mateix link, en el format que vol aquest dispositiu.
 *
 * `auto` es resol amb `device` (per defecte, el d'aquest navegador). Un link que no
 * s'entengui torna tal com ha arribat.
 */
export function rewriteWhatsappLink(
  url: string,
  mode: WhatsappOpenMode,
  device: WhatsappDevice = detectWhatsappDevice(),
): string {
  const target = parseWhatsappLink(url);
  if (!target) return url;

  return formatWhatsappLink(target, resolveWhatsappOpenMode(mode, device));
}

/**
 * La preferència d'aquest navegador. `auto` si no n'hi ha o si no és vàlida.
 *
 * `storage` ja embolcalla la lectura amb `try/catch`: en un navegador amb les dades
 * de lloc bloquejades això ha de tornar `auto`, no petar a mitja acció.
 */
export function readWhatsappOpenMode(): WhatsappOpenMode {
  const saved = storage.get<WhatsappOpenMode>(STORAGE_KEYS.WHATSAPP_OPEN_MODE, 'auto');
  return WHATSAPP_OPEN_MODES.includes(saved) ? saved : 'auto';
}

/** Desa la preferència d'aquest navegador. Només d'aquest: mai va al servidor. */
export function writeWhatsappOpenMode(mode: WhatsappOpenMode): void {
  storage.set(STORAGE_KEYS.WHATSAPP_OPEN_MODE, mode);
}

/**
 * **L'únic lloc** des d'on s'obre WhatsApp (§F9).
 *
 * Tot botó de WhatsApp de l'aplicació ha de passar per aquí: si algun se salta la
 * reescriptura, en Pere en tindrà un que obre el navegador i un altre que obre
 * l'app, i no sabrà per què.
 */
export function openWhatsapp(url: string | null | undefined): void {
  if (!url) return;
  window.open(rewriteWhatsappLink(url, readWhatsappOpenMode()), '_blank', 'noopener');
}
